import { DateTime } from "luxon";
import { ChoiceButton } from "swing-components";

import { type FeedbackRating } from "~components/ModalSubFeedback";
import { Button } from "~components/Molecules";
import { translate } from "~utils";

/********* Types ********/
type SubRepeatReviewPromptProps = {
  subName: string;
  subLastFeedback: { rating: FeedbackRating; date: string };
  onQuickSubmit: () => void;
  openModal: () => void;
  isSubmissionPending?: boolean;
};

/********* Component ********/
export function SubRepeatReviewPrompt(props: SubRepeatReviewPromptProps) {
  const {
    subName,
    subLastFeedback: { rating, date },
    onQuickSubmit,
    openModal,
    isSubmissionPending,
  } = props;

  const formattedDate = DateTime.fromISO(date).toFormat("LLL dd"); // e.g. "Feb 06"

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      {/* Question */}
      <p css={{ fontWeight: 600, margin: 0 }}>
        {translate("GIVE_FEEDBACK_USE_PREVIOUS_REVIEW_LABEL")}
      </p>

      {/* Review Summary */}
      <div css={{ display: "flex", gap: 8, alignItems: "center" }}>
        {/* Width is explicitly set in designs, remains the same for mobile and desktop */}
        <div css={{ width: 99, flexShrink: 0 }}>
          <ChoiceButton
            icon={
              rating === "RATING_POSITIVE"
                ? { name: "Face Positive", color: "green600" }
                : { name: "Face Neutral", color: "yellow600" }
            }
            value={rating}
            isSelected
            activeBgColor={rating === "RATING_POSITIVE" ? "green200" : "yellow200"}
          />
        </div>
        <p
          css={{
            margin: 0,
            fontSize: 12,
            // prevent text from overflowing the container
            overflowWrap: "break-word",
            flex: 1,
            minWidth: 0,
          }}
        >
          {translate("GIVE_FEEDBACK_PAST_REVIEW_SUMMARY_LABEL", {
            subName,
            date: formattedDate,
          })}
        </p>
      </div>

      {/* Buttons */}
      <div css={{ display: "flex", gap: 16 }}>
        <div css={{ flex: 1 }}>
          <Button
            type="SECONDARY"
            onClick={openModal}
            label={translate("GIVE_FEEDBACK_PAST_REVIEW_CHANGE_LABEL")}
            isFluid
            isDisabled={isSubmissionPending}
          />
        </div>
        <div css={{ flex: 1 }}>
          <Button
            type="PRIMARY"
            onClick={onQuickSubmit}
            label={translate("GIVE_FEEDBACK_PAST_REVIEW_KEEP_LABEL")}
            isFluid
            isDisabled={isSubmissionPending}
            isActionPending={isSubmissionPending}
          />
        </div>
      </div>
    </div>
  );
}
